import { clsx } from 'clsx'
import { useRouter, useSearchParams } from 'next/navigation'
import { type FC, type ReactNode, useId, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import { FormControl, FormLabel, Select } from 'base-ui'

import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../../../global-config'
import { urlWithSearchAttribution } from '../../../../../product-search'
import { urlResolverGetPathForPimProduct } from '../../../../../url-handling'
import {
  type AttributeGroupData,
  type SelectionEntry,
} from '../../../product-page-context/AttributeGroup'
import { type ProductPageContextAttributeSelectionControls } from '../../../product-page-context/useProductPageAttributeSelectionControls'
import { useProductPageContext } from '../../../productPageContext'
import { ProductPageAttributeHead } from '../ProductPageAttributeHead'
import { productPageGetAttributeSelectionEntryConditions } from '../ProductPageAttributeSelector/productPageGetAttributeSelectionEntryConditions'

import { ProductPageContactLensesInfoButton } from './ProductPageContactLensesInfoButton'
import { ProductPageListTypeAttributeGroupSelectOption } from './ProductPageListTypeAttributeGroupSelectOption'

type ProductPageListTypeAttributeGroupProps = {
  attributeGroup: AttributeGroupData
}

/**
 * if selection entry contains candidate and other conditions, so it's selected,
 * will navigate clientside to the candidate, effectively selecting it as current variant
 */
const navigateToCandidateIfNeeded = ({
  globalConfig,
  router,
  searchParams,
  selectionEntry,
}: {
  globalConfig: PublicGlobalConfig
  router: ReturnType<typeof useRouter>
  searchParams: ReturnType<typeof useSearchParams>
  selectionEntry: SelectionEntry
}): void => {
  if (selectionEntry.isSelected) {
    return
  }

  const conditions =
    productPageGetAttributeSelectionEntryConditions(selectionEntry)

  if (
    !conditions.candidateVariant ||
    conditions.dontSelectCandidateOnClick ||
    !conditions.showAsLink
  ) {
    return
  }

  router.push(
    urlWithSearchAttribution(
      urlResolverGetPathForPimProduct(
        globalConfig,
        conditions.candidateVariant,
      ),
      searchParams,
    ),
    { scroll: false },
  )
}

const handleChange = ({
  attributeSelectionControls,
  entryToSelect,
  globalConfig,
  router,
  searchParams,
}: {
  attributeSelectionControls: ProductPageContextAttributeSelectionControls
  entryToSelect: SelectionEntry | null
  globalConfig: PublicGlobalConfig
  router: ReturnType<typeof useRouter>
  searchParams: ReturnType<typeof useSearchParams>
}): void => {
  if (!entryToSelect) {
    return
  }

  attributeSelectionControls.handleAttributeSelect(entryToSelect)

  /**
   * other attributes except list have link for candidate, so browser navigates and new currentVariant is set,
   * in case of list, we need to manually navigate if new candidate variant is matched
   */
  navigateToCandidateIfNeeded({
    globalConfig,
    router,
    searchParams,
    selectionEntry: entryToSelect,
  })
}

export const ProductPageListTypeAttributeGroup: FC<
  ProductPageListTypeAttributeGroupProps
> = ({ attributeGroup }) => {
  const globalConfig = useGlobalConfigContext()
  const {
    metaAttributeData,
    selectionEntries,
    shouldDisplayOnlySelectedValue,
  } = attributeGroup
  const { attributeSelectionControls } = useProductPageContext()
  const router = useRouter()
  const searchParams = useSearchParams()

  const attributesSelectId = useId()

  const isContactLensesGroup = metaAttributeData.group === 'contact_lenses'

  const selectedEntry = selectionEntries.find((entry) => entry.isSelected)

  const sortedSelectionEntries = useMemo(() => {
    if (isContactLensesGroup) {
      return selectionEntries.toSorted((entryOne, entryTwo) => {
        const valueTwo = entryTwo.attribute.value
          ? Number(entryTwo.attribute.value.replace(',', '.'))
          : 0
        const valueOne = entryOne.attribute.value
          ? Number(entryOne.attribute.value.replace(',', '.'))
          : 0

        return valueTwo - valueOne
      })
    }

    return selectionEntries
  }, [selectionEntries, isContactLensesGroup])

  if (shouldDisplayOnlySelectedValue) {
    return (
      <ProductPageAttributeHead
        icon={
          isContactLensesGroup ? (
            <ProductPageContactLensesInfoButton
              variantAttribute={metaAttributeData.variant_attribute}
            />
          ) : null
        }
        metaAttribute={metaAttributeData}
        selectedEntry={selectedEntry}
      />
    )
  }

  return (
    <div className="flex items-center gap-4 py-3.5 first:pt-0">
      <FormControl className="flex-1" value={selectedEntry}>
        <FormLabel
          className="top-1 after:content-[':']"
          htmlFor={attributesSelectId}
        >
          {metaAttributeData.label}
        </FormLabel>
        <Select
          onChange={(event, selectionEntry): void => {
            handleChange({
              attributeSelectionControls,
              entryToSelect: selectionEntry,
              globalConfig,
              router,
              searchParams,
            })
          }}
          renderValue={(): ReactNode => {
            if (!selectedEntry) {
              return (
                <span className="text-dark-primary-strong">
                  <FormattedMessage id="variantAttributes.select.pleaseSelect.label" />
                </span>
              )
            }

            const { selectedButNotBuyable, selectedButNoVariants } =
              productPageGetAttributeSelectionEntryConditions(selectedEntry)

            return (
              <span
                className={clsx(
                  (selectedButNotBuyable || selectedButNoVariants) &&
                    'text-dark-primary-strong',
                )}
              >
                {selectedEntry.attribute.value}
              </span>
            )
          }}
          slotProps={{
            listbox: {
              title: metaAttributeData.label,
            },
            root: {
              'aria-labelledby': attributesSelectId,
            },
          }}
          value={selectedEntry}
        >
          {sortedSelectionEntries.map((selectionEntry) => {
            return (
              <ProductPageListTypeAttributeGroupSelectOption
                key={selectionEntry.attribute.code}
                value={selectionEntry}
              />
            )
          })}
        </Select>
      </FormControl>
      {isContactLensesGroup ? (
        <ProductPageContactLensesInfoButton
          variantAttribute={metaAttributeData.variant_attribute}
        />
      ) : null}
    </div>
  )
}
